import React, { useState, useEffect } from 'react';
import { Card, Spin, DatePicker, Empty } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const NewsDistributionChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'days'), dayjs()]);

  const rangePresets = [
    { label: '最近7天', value: [dayjs().subtract(7, 'd'), dayjs()] },
    { label: '最近14天', value: [dayjs().subtract(14, 'd'), dayjs()] },
    { label: '最近30天', value: [dayjs().subtract(30, 'd'), dayjs()] },
    { label: '最近90天', value: [dayjs().subtract(90, 'd'), dayjs()] },
  ];

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    if (!dateRange) return;
    setLoading(true);
    try {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      const res = await axios.get('/api/reports/stats/news-distribution', {
        params: { startDate: start, endDate: end }
      });
      
      // Transform data: Aggregate counts by hour across all dates
      // API returns: { "YYYY-MM-DD": { "0": 1, "1": 2, ... } }
      // We want: [ { hour: "00:00", count: total_0 }, { hour: "01:00", count: total_1 }, ... ]
      
      const hourlyTotals = new Array(24).fill(0);
      
      Object.values(res.data).forEach(dayHours => {
        Object.entries(dayHours).forEach(([hour, count]) => {
          const h = parseInt(hour);
          if (h >= 0 && h < 24) {
            hourlyTotals[h] += count;
          }
        });
      });

      const chartData = hourlyTotals.map((count, index) => ({
        hour: `${String(index).padStart(2, '0')}:00`,
        count,
        // Optional: color intensity based on count could be done here if needed
      }));

      setData(chartData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>时间: {label}</p>
          <p style={{ margin: 0 }}>新闻数量: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card title="新闻发布时间分布 (24小时累计)" extra={
      <RangePicker 
        value={dateRange} 
        onChange={setDateRange} 
        allowClear={false}
        presets={rangePresets}
      />
    } style={{ marginBottom: 24 }}>
      {loading ? <Spin style={{ display: 'block', margin: '50px auto' }} /> : data.length === 0 || data.every(d => d.count === 0) ? <Empty description="暂无数据" /> : (
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            <BarChart 
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="hour" 
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                allowDecimals={false}
                label={{ value: '数量', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
              <Bar dataKey="count" fill="#1890ff" radius={[4, 4, 0, 0]} name="新闻数量" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

export default NewsDistributionChart;